'use client'

import { ChangeEvent, forwardRef } from 'react'

import { AttentionRoundIcon } from '@/components/icons'
import { maskJs } from '@/utils/mask/mask'
import { maskCurrencyChange, regexNumber } from '@/utils/regex'

import GenericCloneComponent from '../GenericCloneComponent/GenericCloneComponent'
import { InputProps } from './types'

import { Loading } from '..'

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type,
      name,
      helperText,
      iconLeft,
      iconRight,
      mask,
      error,
      currencyType = 'R$ ',
      isCurrency,
      onlyChars,
      onlyNums,
      label,
      wFull = false,
      className = '',
      classNameIconRight = '',
      classNameIconLeft = '',
      isLoading,
      componentRight,
      isPercentage,
      ...props
    },
    ref,
  ) => {
    const isError = error || helperText

    // CURRENCY
    const floatToMoney = (num: string): string => {
      const number = parseFloat(num)

      if (Number.isNaN(number)) return '0,00'

      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(number)
    }

    const handleCurrencyChange = (event: ChangeEvent<HTMLInputElement>) => {
      let { value: fieldValue }: { value: string } = event.target
      fieldValue = fieldValue.replace(regexNumber, '')

      if (fieldValue?.length === 1) {
        fieldValue = `0,0${fieldValue}`
      } else if (fieldValue?.length === 2) {
        fieldValue = `0,${fieldValue}`
      } else if (fieldValue) {
        fieldValue = fieldValue.replace(maskCurrencyChange, '$1.$2')
        fieldValue = floatToMoney(fieldValue)
      }

      fieldValue = `${currencyType}${fieldValue}`

      if (fieldValue === currencyType) {
        fieldValue = `${currencyType}0,00`
      }

      return fieldValue
    }

    // PERCENTAGE
    const handleValueWithPercentage = (
      event: ChangeEvent<HTMLInputElement>,
    ) => {
      let inputValue = event.target.value.replace(regexNumber, '')

      inputValue = inputValue ? `${inputValue}%` : inputValue

      event.target.value = inputValue
      event.target.selectionStart = inputValue?.length - 1
      event.target.selectionEnd = inputValue?.length - 1
    }

    // MASK-JS LIB
    const handleValuesWithMask = (
      value: string,
      maskOption: string | Array<string>,
    ) => {
      if (typeof maskOption === 'string') {
        let valueReplaced = ''
        if (onlyNums) {
          valueReplaced = value.replace(/\D/g, '')
        }
        if (onlyChars) {
          valueReplaced = value.replace(/\d/g, '')
        }

        return maskJs(maskOption, valueReplaced || value)
      }

      if (Array.isArray(maskOption)) {
        const sortedMasks = [...maskOption].sort((a, b) => a.length - b.length)
        const alphanumericLength = (value.match(/[A-Za-z0-9]/g) || []).length

        let currentMask = sortedMasks[0]

        for (let i = 0; i < sortedMasks.length - 1; i++) {
          const maskCapacity = (sortedMasks[i].match(/[9a*]/g) || []).length
          if (alphanumericLength > maskCapacity) {
            currentMask = sortedMasks[i + 1]
          } else {
            break
          }
        }

        let valueReplaced = value
        if (onlyNums) valueReplaced = value.replace(/\D/g, '')
        if (onlyChars) valueReplaced = value.replace(/\d/g, '')

        return maskJs(currentMask, valueReplaced)
      }

      return maskJs(maskOption, value)
    }

    // DETERMINES WHICH METHOD TO USE
    const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
      if (isPercentage) handleValueWithPercentage(event)
      if (isCurrency) event.target.value = handleCurrencyChange(event)
      if (mask && !isCurrency && !isPercentage)
        event.target.value = handleValuesWithMask(event.target.value, mask)
      if (onlyNums && !mask && !isCurrency && !isPercentage)
        event.target.value = event.target.value.replace(/\D/g, '')
    }

    return (
      <div
        className={`flex flex-col h-max gap-2
          ${className}
          ${wFull ? 'w-full' : ''}
        `}
      >
        {label ? (
          <label
            htmlFor={props.id ?? name}
            className="prose-body text-black w-full"
          >
            {label}
          </label>
        ) : null}
        <div
          className={`flex gap-4 items-center relative ${wFull ? 'w-full' : ''}`}
        >
          <input
            id={props.id ?? name}
            className={`border peer rounded-lg prose-body py-3.5
            ${isError ? 'focus:outline-status-red' : 'focus:outline-primary-500'}
            ${isError ? 'border-status-red' : 'border-grayscale-200'}
            ${iconLeft ? 'pl-12' : 'pl-4'}
            ${iconRight || isLoading ? 'pr-12' : 'pr-4'}
            ${wFull ? 'w-full' : ''}
            `}
            type={type}
            name={name}
            ref={ref}
            {...props}
            onInput={handleChangeInput}
          />

          {iconLeft ? (
            <GenericCloneComponent
              propsMapper={() => ({
                className: `flex absolute items-center left-4 fill-grayscale-200
                  ${isError ? 'peer-focus:fill-status-red' : 'peer-focus:fill-primary-500'}
                  ${classNameIconLeft}`,
              })}
            >
              {iconLeft}
            </GenericCloneComponent>
          ) : null}

          {isLoading ? (
            <div className="absolute w-6 h-6 right-4">
              <Loading variant="secondary" />
            </div>
          ) : null}

          {iconRight ? (
            <GenericCloneComponent
              propsMapper={() => ({
                className: `flex absolute items-center right-4 fill-grayscale-200
                ${isError ? 'peer-focus:fill-status-red' : 'peer-focus:fill-primary-500'}
                ${classNameIconRight}`,
              })}
            >
              {iconRight}
            </GenericCloneComponent>
          ) : null}

          {componentRight ? componentRight : null}
        </div>
        {helperText && (
          <span className="flex gap-1 items-center text-status-red prose-caption">
            <AttentionRoundIcon className="fill-status-red w-4 h-4" />
            {helperText}
          </span>
        )}
      </div>
    )
  },
)

Input.displayName = 'Input'

export default Input
